import useGameState from "app/game/useGameState";
import ExitGameButton from "app/top-nav/ExitGameButton";
import GameMenu from "app/top-nav/GameMenu";
import GameTimer from "app/top-nav/GameTimer";
import cn from "app/util/classnames";
import mixpanel from "mixpanel-browser";
import { type GameMenuHandle } from "app/top-nav/GameMenu";
import { useRef } from "react";
import useTutorialState from "app/tutorial/useTutorialState";

const MENU_OPTION_CLASS = "cursor-pointer hover:bg-green-light";

interface TopNavProps {
  hideMenuButton: boolean;
  showGameTimer: boolean;
  onHowToPlayClick?: () => void;
  onTutorialSelectClick?: () => void;
  onSettingsClick?: () => void;
  onCreditsClick?: () => void;
  onQuitTutorialClick?: () => void;
}

export default function TopNav(props: TopNavProps) {
  const { id: game_id, showPlayTime } = useGameState();

  const gameMenuRef = useRef<GameMenuHandle>(null);

  // Note: We decided not to show the game timer since players felt stressed
  // out by it during testing. In the future, there will be a settings menu
  // that may include the option to enable the timer, in which case this
  // variable should take that setting and props.showGameTimer into account.
  const hideTimer = !props.showGameTimer || !showPlayTime;

  const { isInTutorial } = useTutorialState();

  function handleGameMenuOptionClick(callback?: () => void) {
    gameMenuRef.current?.close();
    callback?.();
  }

  function handleReportBugClick() {
    mixpanel.track("bug_report", {
      game_id,
    });
    gameMenuRef.current?.close();
  }

  return (
    <div className="w-full basis-1 shrink bg-green-mid">
      {/* Div used to hide buttons*/}
      <div className="grid h-10 grid-cols-[2.5rem_1fr_2.5rem] items-center justify-between px-1 md:grid-cols-[12rem_1fr_12rem]">
        <ExitGameButton hideText className="justify-self-start" />
        <div className={cn("flex-1 flex justify-center")}>
          <div className={cn(hideTimer && "hidden")}>
            <GameTimer pauseTimer={!props.showGameTimer} />
          </div>
        </div>
        <div className="justify-self-end flex items-center justify-end gap-4">
          {!props.hideMenuButton && (
            <GameMenu ref={gameMenuRef}>
              {!isInTutorial && (
                <>
                  <button
                    className={MENU_OPTION_CLASS}
                    role="menuitem"
                    onClick={() =>
                      handleGameMenuOptionClick(props.onHowToPlayClick)
                    }
                  >
                    How to Play
                  </button>
                  <button
                    className={MENU_OPTION_CLASS}
                    role="menuitem"
                    onClick={() =>
                      handleGameMenuOptionClick(props.onTutorialSelectClick)
                    }
                  >
                    Tutorials
                  </button>
                </>
              )}
              <button
                className={MENU_OPTION_CLASS}
                role="menuitem"
                onClick={() => handleGameMenuOptionClick(props.onSettingsClick)}
              >
                Settings
              </button>
              <button
                className={MENU_OPTION_CLASS}
                role="menuitem"
                onClick={() => handleGameMenuOptionClick(props.onCreditsClick)}
              >
                Credits
              </button>
              <a
                role="menuitem"
                className={cn(
                  "normal-case flex items-center justify-center",
                  MENU_OPTION_CLASS,
                )}
                onClick={handleReportBugClick}
                href="https://docs.google.com/forms/d/e/1FAIpQLScxQWHzzcu2QXbsNTgdojs-VwS8rZswZ6ougNUN-e_sWJQfkw/viewform?usp=dialog"
                target="_blank"
              >
                Report a Bug
              </a>
              {isInTutorial && (
                <button
                  className={MENU_OPTION_CLASS}
                  role="menuitem"
                  onClick={() =>
                    handleGameMenuOptionClick(props.onQuitTutorialClick)
                  }
                >
                  Quit Tutorial
                </button>
              )}
            </GameMenu>
          )}
        </div>
      </div>
    </div>
  );
}
